import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  CalendarDays, Users, Scissors, BarChart3, Bell, Shield,
  ArrowRight, Star, Check, Globe, Smartphone, Clock, QrCode, Zap,
  ChevronRight, X, Menu
} from 'lucide-react';

const CALENDAR_IMG = '/images/calendar-mockup.png';
const BOOKING_IMG = '/images/booking-mockup.png';
const DASHBOARD_IMG = '/images/dashboard-mockup.png';

const featureIcons = [CalendarDays, Users, Scissors, Globe, Bell, BarChart3, Shield, QrCode];
const featureKeys = ['smartCalendar', 'clientManagement', 'serviceMenu', 'onlineBooking', 'reminders', 'reports', 'staffPermissions', 'qrCode'];

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="landing-page">
      {/* Nav */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#556B2F] tracking-tight" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-[#5C6656] hover:text-[#556B2F] transition-colors">{t('nav.features')}</a>
            <a href="#pricing" className="text-sm text-[#5C6656] hover:text-[#556B2F] transition-colors">{t('nav.pricing')}</a>
            <a href="#how-it-works" className="text-sm text-[#5C6656] hover:text-[#556B2F] transition-colors">{t('nav.howItWorks')}</a>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher variant="nav" />
            <Button variant="ghost" onClick={() => navigate('/get-started')} className="text-[#556B2F] font-medium" data-testid="nav-signin">{t('nav.signIn')}</Button>
            <Button onClick={() => navigate('/login?tab=register')} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white rounded-full px-6" data-testid="nav-start">
              {t('nav.startFreeTrial')}
            </Button>
          </div>
          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher variant="nav" />
            <button className="p-2" onClick={() => setMobileMenu(!mobileMenu)} data-testid="landing-mobile-menu">
              {mobileMenu ? <X size={22} className="text-[#1A2421]" /> : <Menu size={22} className="text-[#1A2421]" />}
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-[#E5E7E1] px-6 py-4 space-y-3 animate-in slide-in-from-top">
            <a href="#features" onClick={() => setMobileMenu(false)} className="block text-sm text-[#5C6656] py-2">{t('nav.features')}</a>
            <a href="#pricing" onClick={() => setMobileMenu(false)} className="block text-sm text-[#5C6656] py-2">{t('nav.pricing')}</a>
            <a href="#how-it-works" onClick={() => setMobileMenu(false)} className="block text-sm text-[#5C6656] py-2">{t('nav.howItWorks')}</a>
            <Button onClick={() => navigate('/login')} variant="ghost" className="w-full justify-start text-[#556B2F]">{t('nav.signIn')}</Button>
            <Button onClick={() => navigate('/login?tab=register')} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">{t('nav.startFreeTrial')}</Button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-16 sm:pt-32 sm:pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E9EDDF] text-[#556B2F] text-sm font-medium mb-6 animate-fade-in">
              <Zap size={14} /> {t('hero.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-[#1A2421] leading-[1.05] tracking-tight" style={{ fontFamily: 'Outfit' }}>
              {t('hero.title1')}<br /><span className="text-[#556B2F]">{t('hero.title2')}</span>
            </h1>
            <p className="text-base sm:text-lg text-[#5C6656] mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope' }}>{t('hero.desc')}</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mt-8">
              <Button onClick={() => navigate('/login?tab=register')} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white rounded-full px-8 py-6 text-base w-full sm:w-auto" data-testid="hero-cta">
                {t('hero.cta')} <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/book/demo-business')} className="border-[#556B2F] text-[#556B2F] rounded-full px-8 py-6 text-base w-full sm:w-auto" data-testid="hero-demo">
                {t('hero.demo')}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-8 text-sm text-[#5C6656]">
              <span className="flex items-center gap-1"><Check size={16} className="text-[#556B2F]" /> {t('hero.check1')}</span>
              <span className="flex items-center gap-1"><Check size={16} className="text-[#556B2F]" /> {t('hero.check2')}</span>
              <span className="flex items-center gap-1"><Check size={16} className="text-[#556B2F]" /> {t('hero.check3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Phone Mockups Showcase */}
      <section className="py-16 sm:py-20 px-6 bg-white border-b border-[#E5E7E1]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('phoneMockups.title')}</h2>
            <p className="text-[#5C6656] mt-3 text-sm sm:text-base max-w-lg mx-auto">{t('phoneMockups.desc')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-center">
            {[
              { img: CALENDAR_IMG, title: t('phoneMockups.calendar'), desc: t('phoneMockups.calendarDesc') },
              { img: BOOKING_IMG, title: t('phoneMockups.booking'), desc: t('phoneMockups.bookingDesc') },
              { img: DASHBOARD_IMG, title: t('phoneMockups.dashboard'), desc: t('phoneMockups.dashboardDesc') },
            ].map((item, i) => (
              <div key={i} className="text-center group" data-testid={`phone-mockup-${i}`}>
                <div className="relative mx-auto mb-6 overflow-hidden rounded-2xl bg-[#F9F8F6] shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2" style={{ maxWidth: 240 }}>
                  <img src={item.img} alt={item.title} className="w-full h-auto" loading="lazy" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1A2421] mb-1" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                <p className="text-sm text-[#5C6656] max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-8 border-y border-[#E5E7E1] bg-white">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
          {[
            { val: '10k+', label: t('stats.appointments') },
            { val: '500+', label: t('stats.businesses') },
            { val: '99.9%', label: t('stats.uptime') },
            { val: '4.9', label: t('stats.rating'), extra: true },
          ].map(s => (
            <div key={s.label}>
              <p className="text-2xl sm:text-3xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>
                {s.val}{s.extra && <Star size={14} className="inline ml-1 text-[#D4A373] fill-[#D4A373]" />}
              </p>
              <p className="text-xs sm:text-sm text-[#5C6656] mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-20 px-6" id="features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('features.title')}</h2>
            <p className="text-[#5C6656] mt-3 text-sm sm:text-base max-w-lg mx-auto">{t('features.desc')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featureKeys.map((key, i) => {
              const Icon = featureIcons[i];
              return (
                <Card key={key} data-testid={`feature-card-${i}`}
                  className={`border-[#E5E7E1] cursor-default transition-all duration-300 ${hoveredFeature === i ? 'border-[#556B2F] shadow-lg -translate-y-1' : 'shadow-sm hover:shadow-md'}`}
                  onMouseEnter={() => setHoveredFeature(i)} onMouseLeave={() => setHoveredFeature(null)}>
                  <CardContent className="p-5 sm:p-6">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 ${hoveredFeature === i ? 'bg-[#556B2F] text-white' : 'bg-[#E9EDDF] text-[#556B2F]'}`}>
                      <Icon size={22} />
                    </div>
                    <h3 className="font-semibold text-[#1A2421] mb-1" style={{ fontFamily: 'Outfit' }}>{t(`features.${key}`)}</h3>
                    <p className="text-sm text-[#5C6656] leading-relaxed">{t(`features.${key}Desc`)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20 px-6 bg-white border-y border-[#E5E7E1]" id="how-it-works">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('howItWorks.title')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map(n => (
              <div key={n} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#556B2F] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4" style={{ fontFamily: 'Outfit' }}>
                  {String(n).padStart(2, '0')}
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1A2421] mb-2" style={{ fontFamily: 'Outfit' }}>{t(`howItWorks.step${n}Title`)}</h3>
                <p className="text-sm text-[#5C6656]">{t(`howItWorks.step${n}Desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Switching section */}
      <section className="py-16 sm:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#556B2F] rounded-3xl p-8 sm:p-10 lg:p-14 text-center text-white">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>{t('switching.title')}</h2>
            <p className="text-white/80 text-sm sm:text-base max-w-lg mx-auto mb-8">{t('switching.desc')}</p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6 text-sm">
              {['item1', 'item2', 'item3', 'item4'].map(key => (
                <span key={key} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Check size={14} /> {t(`switching.${key}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 px-6 bg-white border-y border-[#E5E7E1]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A2421] text-center mb-10 sm:mb-12" style={{ fontFamily: 'Outfit' }}>{t('testimonials.title')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map(n => (
              <Card key={n} className="border-[#E5E7E1] shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-0.5 mb-3">
                    {[1,2,3,4,5].map(i => <Star key={i} size={14} className="text-[#D4A373] fill-[#D4A373]" />)}
                  </div>
                  <p className="text-sm text-[#5C6656] leading-relaxed mb-4 italic">"{t(`testimonials.t${n}`)}"</p>
                  <div>
                    <p className="font-medium text-[#1A2421] text-sm">{t(`testimonials.t${n}Name`)}</p>
                    <p className="text-xs text-[#5C6656]">{t(`testimonials.t${n}Role`)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-20 px-6" id="pricing">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A2421] mb-3" style={{ fontFamily: 'Outfit' }}>{t('pricing.title')}</h2>
          <p className="text-[#5C6656] text-sm sm:text-base mb-10 sm:mb-12">{t('pricing.desc')}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              {
                id: 'basic', name: t('pricing.basicName'), price: 0, desc: t('pricing.basicDesc'),
                features: [t('pricing.basicF1'), t('pricing.basicF2'), t('pricing.basicF3'), t('pricing.basicF4'), t('pricing.basicF5')],
                excluded: [t('pricing.starterF4'), t('pricing.businessF3')],
              },
              {
                id: 'starter', name: t('pricing.starterName'), price: 25, desc: t('pricing.starterDesc'),
                features: [t('pricing.starterF1'), t('pricing.starterF2'), t('pricing.starterF3'), t('pricing.starterF4'), t('pricing.starterF5'), t('pricing.starterF6'), t('pricing.starterF7')],
                excluded: [],
              },
              {
                id: 'business', name: t('pricing.proName'), price: 45, desc: t('pricing.proDesc'), popular: true,
                features: [t('pricing.businessF1'), t('pricing.businessF2'), t('pricing.businessF3'), t('pricing.businessF4'), t('pricing.businessF5'), t('pricing.businessF6'), t('pricing.businessF7'), t('pricing.businessF8')],
                excluded: [],
              },
              {
                id: 'premium', name: t('pricing.businessName'), price: 75, desc: t('pricing.businessDesc'), mostComplete: true,
                features: [t('pricing.premiumF1'), t('pricing.premiumF2'), t('pricing.premiumF3'), t('pricing.premiumF4'), t('pricing.premiumF5'), t('pricing.premiumF6'), t('pricing.premiumF7'), t('pricing.premiumF8'), t('pricing.premiumF9'), t('pricing.premiumF10')],
                excluded: [],
              },
            ].map(tier => (
              <Card key={tier.id} data-testid={`tier-${tier.id}`}
                className={`shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1 relative ${tier.popular ? 'border-[#556B2F] border-2' : tier.mostComplete ? 'border-[#D4A373] border-2' : 'border-[#E5E7E1]'}`}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#556B2F] text-white text-xs font-bold px-4 py-1 rounded-full">{t('pricing.mostPopular')}</div>
                )}
                {tier.mostComplete && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A373] text-white text-[11px] font-bold px-3 py-1 rounded-full whitespace-nowrap">{t('pricing.mostComplete') || 'MOST COMPREHENSIVE'}</div>
                )}
                <CardContent className="p-6 sm:p-7 text-left">
                  <h3 className="text-base sm:text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{tier.name}</h3>
                  <p className="text-xs text-[#5C6656] mt-1">{tier.desc}</p>
                  <p className="mt-4" style={{ fontFamily: 'Outfit' }}>
                    <span className={`text-3xl sm:text-4xl font-bold ${tier.popular ? 'text-[#556B2F]' : tier.mostComplete ? 'text-[#B8864D]' : 'text-[#1A2421]'}`}>
                      {tier.price === 0 ? t('pricing.free') : `\u00A3${tier.price}`}
                    </span>
                    {tier.price > 0 && <span className="text-sm font-normal text-[#5C6656]">{t('pricing.perMonth')}</span>}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {tier.features.map((f, fi) => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${tier.mostComplete && fi < 2 ? 'text-[#1A2421] font-semibold' : 'text-[#5C6656]'}`}><Check size={14} className={`${tier.mostComplete ? 'text-[#D4A373]' : 'text-[#556B2F]'} mt-0.5 flex-shrink-0`} />{f}</li>
                    ))}
                    {tier.excluded.map(f => (
                      <li key={f} className="flex items-start gap-2 text-sm text-[#B0B5A8] line-through"><X size={14} className="text-[#B0B5A8] mt-0.5 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <Button onClick={() => navigate('/login?tab=register')}
                    className={`w-full mt-6 ${tier.popular ? 'bg-[#556B2F] hover:bg-[#4A5D23] text-white' : tier.mostComplete ? 'bg-[#D4A373] hover:bg-[#B8864D] text-white' : 'bg-[#E9EDDF] text-[#556B2F] hover:bg-[#556B2F] hover:text-white'}`}
                    data-testid={`tier-cta-${tier.id}`}>
                    {tier.price === 0 ? t('pricing.getStarted') : t('pricing.startTrial')} <ChevronRight size={16} className="ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-[#5C6656] mt-6">{t('pricing.note')}</p>
          <div className="mt-8 max-w-2xl mx-auto bg-[#1A2421] rounded-2xl p-6 text-center" data-testid="no-fees-banner">
            <p className="text-white text-sm sm:text-base font-medium leading-relaxed">{t('pricing.noHidden')}</p>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-16 sm:py-20 px-6 bg-white border-y border-[#E5E7E1]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E9EDDF] text-[#556B2F] text-xs font-bold mb-4">
                <Smartphone size={12} /> {t('mobile.badge')}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>{t('mobile.title')}</h2>
              <p className="text-sm sm:text-base text-[#5C6656] leading-relaxed mb-6">{t('mobile.desc')}</p>
              <div className="flex flex-wrap gap-3">
                {['iOS Safari', 'Android Chrome', 'Desktop', 'Tablet'].map(d => (
                  <span key={d} className="flex items-center gap-1.5 text-xs font-medium text-[#556B2F] bg-[#E9EDDF] px-3 py-1.5 rounded-full">
                    <Check size={12} /> {d}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex gap-4 items-end">
                <div className="w-36 rounded-2xl overflow-hidden shadow-xl border-2 border-[#E5E7E1] hover:-translate-y-2 transition-transform duration-300">
                  <img src={CALENDAR_IMG} alt="Calendar on mobile" className="w-full h-auto" loading="lazy" />
                </div>
                <div className="w-44 rounded-2xl overflow-hidden shadow-2xl border-2 border-[#556B2F] hover:-translate-y-2 transition-transform duration-300">
                  <img src={BOOKING_IMG} alt="Booking on mobile" className="w-full h-auto" loading="lazy" />
                </div>
                <div className="w-36 rounded-2xl overflow-hidden shadow-xl border-2 border-[#E5E7E1] hover:-translate-y-2 transition-transform duration-300">
                  <img src={DASHBOARD_IMG} alt="Dashboard on mobile" className="w-full h-auto" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>{t('cta.title')}</h2>
          <p className="text-sm sm:text-base text-[#5C6656] mb-8">{t('cta.desc')}</p>
          <Button onClick={() => navigate('/login?tab=register')} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white rounded-full px-10 py-6 text-base" data-testid="footer-cta">
            {t('cta.button')} <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 px-6 border-t border-[#E5E7E1] bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>Apptio</h2>
            <span className="text-xs text-[#5C6656]">&copy; {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-6 text-xs sm:text-sm text-[#5C6656]">
            <span>{t('footer.tagline')}</span>
            <span className="flex items-center gap-1"><Smartphone size={12} /> {t('footer.mobile')}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
